import { useMemo } from 'react';
import { RepasItem } from './AlimentSelector';

interface RepasListProps {
  repas: RepasItem[];
  onQuantiteChange: (id: number, quantite: number) => void;
  onRemove: (id: number) => void;
}

const igColor = (ig: number) => {
  if (ig === 0) return 'text-gray-400';
  if (ig < 40) return 'text-emerald-600';
  if (ig < 60) return 'text-amber-600';
  return 'text-red-600';
};

export default function RepasList({ repas, onQuantiteChange, onRemove }: RepasListProps) {
  const totalGlucides = useMemo(() =>
    Math.round(repas.reduce((sum, r) => sum + (r.aliment.glucidesPour100g * r.quantite) / 100, 0)),
    [repas]
  );

  const totalCalories = useMemo(() =>
    Math.round(repas.reduce((sum, r) => sum + (r.aliment.calories100g * r.quantite) / 100, 0)),
    [repas]
  );

  const nutriScore = totalGlucides < 30 ? 'A' : totalGlucides < 50 ? 'B' : totalGlucides < 70 ? 'C' : totalGlucides < 90 ? 'D' : 'E';
  const nutriColors: Record<string, string> = {
    A: 'bg-emerald-500', B: 'bg-lime-500', C: 'bg-yellow-500', D: 'bg-orange-500', E: 'bg-red-500',
  };

  if (repas.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-2xl mb-4">
          <svg className="w-8 h-8 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/>
            <path d="M7 2v20"/>
            <path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>
          </svg>
        </div>
        <p className="font-medium text-gray-500 text-sm">Votre repas est vide</p>
        <p className="text-xs mt-1 text-center max-w-xs">Cliquez sur le <strong>+</strong> d'un aliment pour l'ajouter à votre repas</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Items */}
      <div className="space-y-3">
        {repas.map((item) => {
          const glucidesItem = Math.round((item.aliment.glucidesPour100g * item.quantite) / 100 * 10) / 10;
          const caloriesItem = Math.round((item.aliment.calories100g * item.quantite) / 100);
          return (
            <div key={item.aliment.id} className="bg-white border border-gray-100 rounded-xl p-3">
              <div className="flex items-start gap-3">
                {/* Image */}
                <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0">
                  <img src={item.aliment.image} alt={item.aliment.nom} className="w-full h-full object-cover object-top" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-gray-800 text-sm leading-tight">{item.aliment.nom}</p>
                    <button
                      onClick={() => onRemove(item.aliment.id)}
                      className="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-red-400 cursor-pointer shrink-0 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18"/>
                        <path d="m6 6 12 12"/>
                      </svg>
                    </button>
                  </div>

                  {/* Glucides result */}
                  <div className="flex items-center gap-2 mt-1">
                    {glucidesItem > 0 ? (
                      <span className="text-xs font-bold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">
                        {glucidesItem}g glucides
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">0g glucides</span>
                    )}
                    <span className="text-xs text-gray-400">{caloriesItem} kcal</span>
                    {item.aliment.indexGlycemique > 0 && (
                      <span className={`text-xs font-medium ${igColor(item.aliment.indexGlycemique)}`}>
                        IG {item.aliment.indexGlycemique}
                      </span>
                    )}
                  </div>

                  {/* Quantity input */}
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => onQuantiteChange(item.aliment.id, Math.max(10, item.quantite - 10))}
                      className="w-7 h-7 flex items-center justify-center bg-gray-100 hover:bg-teal-100 text-gray-600 hover:text-teal-600 rounded-lg cursor-pointer transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14"/>
                      </svg>
                    </button>
                    <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1">
                      <input
                        type="number"
                        min={10}
                        max={1000}
                        step={10}
                        value={item.quantite}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          if (!isNaN(val) && val >= 10 && val <= 1000) onQuantiteChange(item.aliment.id, val);
                        }}
                        className="w-12 text-center text-sm font-semibold text-gray-800 outline-none bg-transparent"
                      />
                      <span className="text-xs text-gray-400">g</span>
                    </div>
                    <button
                      onClick={() => onQuantiteChange(item.aliment.id, Math.min(1000, item.quantite + 10))}
                      className="w-7 h-7 flex items-center justify-center bg-gray-100 hover:bg-teal-100 text-gray-600 hover:text-teal-600 rounded-lg cursor-pointer transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14"/>
                        <path d="M12 5v14"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Total */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        {/* Nutri-Score */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-500 font-medium">Nutri-Score estimé</span>
          <div className="flex items-center gap-1">
            {['A', 'B', 'C', 'D', 'E'].map((s) => (
              <div
                key={s}
                className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-bold text-white transition-all ${
                  s === nutriScore ? `${nutriColors[s]} scale-110` : 'bg-gray-200 text-gray-400'
                }`}
              >
                {s}
              </div>
            ))}
          </div>
        </div>

        {/* Total glucides */}
        <div className="bg-gradient-to-r from-teal-500 to-emerald-500 rounded-2xl p-4 text-white">
          <div className="flex items-center justify-between mb-1">
            <span className="text-teal-100 text-sm font-medium">Total glucides</span>
            <span className="text-3xl font-bold">{totalGlucides}g</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-teal-200 text-xs">Calories totales</span>
            <span className="text-teal-100 text-sm font-semibold">{totalCalories} kcal</span>
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-teal-200 text-xs">Aliments sélectionnés</span>
            <span className="text-teal-100 text-sm font-semibold">{repas.length}</span>
          </div>
        </div>

        {/* Conseil si glucides élevés */}
        {totalGlucides > 60 && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2">
            <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/>
                <path d="M9 18h6"/>
                <path d="M10 22h4"/>
              </svg>
            </div>
            <p className="text-amber-700 text-xs leading-relaxed">
              Repas &gt; 60g de glucides. Pensez à réduire les féculents ou à augmenter les légumes pour un meilleur contrôle glycémique.
            </p>
          </div>
        )}

        {/* Reset */}
        <button
          onClick={() => repas.forEach((r) => onRemove(r.aliment.id))}
          className="mt-3 w-full border border-gray-200 text-gray-500 hover:text-red-500 hover:border-red-200 hover:bg-red-50 text-xs font-medium py-2 rounded-xl transition-all cursor-pointer whitespace-nowrap"
        >
          <svg className="w-3.5 h-3.5 inline mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 6h18"/>
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
            <line x1="10" x2="10" y1="11" y2="17"/>
            <line x1="14" x2="14" y1="11" y2="17"/>
          </svg>
          Vider le repas
        </button>
      </div>
    </div>
  );
}
